import { useCallback, useEffect, useRef, useState } from 'react'
import {
    getMediaOwnerControlSnapshot,
    getMediaDockHostedChromeLayer,
    getMediaOwnerPlaybackSnapshot,
    getMediaPlaybackState,
    findMountedOwnerForMediaKey,
    isMediaOriginHostedInline,
    pauseMediaKey,
    requestHostedMediaPlayback,
    seekMediaKey,
    setMediaKeyCaptionsEnabled,
    setMediaKeyPlaybackRate,
    setMediaKeyVolume,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'

// The runtime publishes no timeupdate of its own to this surface, so hosted
// chrome polls it — but only while it is actually advancing.
const HOSTED_PROGRESS_INTERVAL_MS = 250

const EMPTY_SNAPSHOT = {
    isPlaying: false,
    isEnded: false,
    currentTime: 0,
    duration: 0,
    volume: null,
    playbackRate: null,
    captionsAvailable: false,
    captionsEnabled: false,
    hasRuntime: false,
}

function readRuntimeSnapshot(mediaKey, previous) {
    const owner = mediaKey ? findMountedOwnerForMediaKey(mediaKey) : null
    if (!owner) {
        // No runtime yet (nothing has played). Fall back to the persisted
        // position so a fresh surface still shows where it left off.
        const stored = mediaKey ? getMediaPlaybackState(mediaKey) : null
        return {
            ...EMPTY_SNAPSHOT,
            currentTime: Number.isFinite(stored?.currentTime) ? stored.currentTime : 0,
            duration: Number.isFinite(stored?.duration) ? stored.duration : previous?.duration || 0,
        }
    }

    const playback = getMediaOwnerPlaybackSnapshot(owner, previous)
    const control = getMediaOwnerControlSnapshot(owner)
    return {
        isPlaying: Boolean(control && !control.isPaused && !control.isEnded),
        isEnded: Boolean(control?.isEnded),
        currentTime: playback.currentTime,
        duration: playback.duration,
        volume: control?.volume ?? null,
        playbackRate: control?.playbackRate ?? null,
        captionsAvailable: control?.captionsAvailable === true,
        captionsEnabled: control?.captionsEnabled === true,
        hasRuntime: true,
    }
}

function snapshotsEqual(first, second) {
    return (
        first.isPlaying === second.isPlaying &&
        first.isEnded === second.isEnded &&
        first.hasRuntime === second.hasRuntime &&
        first.volume === second.volume &&
        first.playbackRate === second.playbackRate &&
        first.captionsAvailable === second.captionsAvailable &&
        first.captionsEnabled === second.captionsEnabled &&
        Math.abs(first.currentTime - second.currentTime) < 0.02 &&
        Math.abs(first.duration - second.duration) < 0.02
    )
}

/**
 * Drives a media dock hosted runtime from an inline surface.
 *
 * With hosted playback the post never mounts its own `<audio>`/`<video>`: the
 * dock owns the single element and this hook is how the post's transport
 * controls read and command it. That removes the second element, the
 * re-download and the re-seek that navigation used to cost.
 *
 * @param {{
 *   mediaKey: string | null,
 *   enabled: boolean,
 *   originRef?: { current: Element | null },
 *   placement?: 'visual' | 'silent',
 * }} options
 */
export default function useHostedMediaRuntime({
    mediaKey,
    enabled,
    originRef = null,
    placement = 'visual',
}) {
    const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT)
    const [isHostedHere, setIsHostedHere] = useState(false)
    const [chromeLayer, setChromeLayer] = useState(null)
    const snapshotRef = useRef(snapshot)
    snapshotRef.current = snapshot
    const active = Boolean(enabled && mediaKey)

    const refresh = useCallback(() => {
        if (!active) return
        setIsHostedHere(
            Boolean(originRef?.current && isMediaOriginHostedInline(mediaKey, originRef.current)),
        )
        setChromeLayer(getMediaDockHostedChromeLayer())
        const next = readRuntimeSnapshot(mediaKey, snapshotRef.current)
        if (!snapshotsEqual(snapshotRef.current, next)) {
            snapshotRef.current = next
            setSnapshot(next)
        }
    }, [active, mediaKey, originRef])

    // Track changes reset the reading rather than carrying the old position.
    useEffect(() => {
        if (!active) return
        snapshotRef.current = EMPTY_SNAPSHOT
        setSnapshot(EMPTY_SNAPSHOT)
        refresh()
    }, [active, mediaKey, refresh])

    // Dock events cover start/stop; the poll only runs while time is moving.
    useEffect(() => {
        if (!active) return undefined
        const unsubscribeDock = subscribeToMediaDock(refresh)
        const unsubscribePlayback = subscribeToMediaPlaybackState(mediaKey, refresh)
        return () => {
            unsubscribeDock()
            unsubscribePlayback()
        }
    }, [active, mediaKey, refresh])

    useEffect(() => {
        if (!active || !snapshot.isPlaying || typeof window === 'undefined') return undefined
        const interval = window.setInterval(refresh, HOSTED_PROGRESS_INTERVAL_MS)
        return () => window.clearInterval(interval)
    }, [active, refresh, snapshot.isPlaying])

    const play = useCallback(
        (options = {}) => {
            if (!active) return false
            // The dock reuses its runtime and moves it back to this surface.
            // Calling playMediaKey here bypasses placement and can seek an
            // already playing element back to its last persisted position.
            const requested = requestHostedMediaPlayback(mediaKey, {
                originEl: originRef?.current || null,
                placement,
                startAtBeginning: options.startAtBeginning === true,
                source: options.source || 'inline',
            })
            refresh()
            return requested
        },
        [active, mediaKey, originRef, placement, refresh],
    )

    const pause = useCallback(() => {
        if (!active) return
        pauseMediaKey(mediaKey)
        refresh()
    }, [active, mediaKey, refresh])

    const toggle = useCallback(() => {
        if (!active) return
        if (snapshotRef.current.isPlaying) pause()
        else play({ startAtBeginning: snapshotRef.current.isEnded })
    }, [active, pause, play])

    const seekTo = useCallback(
        nextTime => {
            if (!active || !Number.isFinite(nextTime)) return
            seekMediaKey(mediaKey, Math.max(0, nextTime))
            refresh()
        },
        [active, mediaKey, refresh],
    )

    const setVolume = useCallback(
        nextVolume => {
            if (!active || !Number.isFinite(nextVolume)) return
            setMediaKeyVolume(mediaKey, nextVolume)
            refresh()
        },
        [active, mediaKey, refresh],
    )

    const setPlaybackRate = useCallback(
        nextRate => {
            if (!active || !Number.isFinite(nextRate)) return
            setMediaKeyPlaybackRate(mediaKey, nextRate)
            refresh()
        },
        [active, mediaKey, refresh],
    )

    const setCaptionsEnabled = useCallback(
        enabled => {
            if (!active || typeof enabled !== 'boolean') return
            setMediaKeyCaptionsEnabled(mediaKey, enabled)
            refresh()
        },
        [active, mediaKey, refresh],
    )

    return {
        active,
        snapshot,
        isHostedHere: active && isHostedHere,
        chromeLayer,
        play,
        pause,
        toggle,
        seekTo,
        setVolume,
        setPlaybackRate,
        setCaptionsEnabled,
        refresh,
    }
}
